import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { timeEntriesApi } from '../../api/timeEntries';
import toast from 'react-hot-toast';
import { Play, Square } from 'lucide-react';

interface TimerControlsProps {
  ticketId: string;
}

export default function TimerControls({ ticketId }: TimerControlsProps) {
  const queryClient = useQueryClient();

  const { data: currentEntry } = useQuery({
    queryKey: ['timer-current'],
    queryFn: () => timeEntriesApi.getCurrentTimer(),
  });

  const startMutation = useMutation({
    mutationFn: () => timeEntriesApi.startTimer({ ticketId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timer-current'] });
      queryClient.invalidateQueries({ queryKey: ['time-entries', ticketId] });
      toast.success('Timer started');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const stopMutation = useMutation({
    mutationFn: () => timeEntriesApi.stopTimer(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timer-current'] });
      queryClient.invalidateQueries({ queryKey: ['time-entries', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      toast.success('Timer stopped');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const isRunningThisTicket = currentEntry && (currentEntry as any).ticketId === ticketId;

  if (isRunningThisTicket) {
    const start = (currentEntry as any).startTime ? new Date((currentEntry as any).startTime).getTime() : Date.now();
    const elapsed = Math.floor((Date.now() - start) / 60000);

    return (
      <div
        className="flex items-center gap-2 p-2 rounded-lg border"
        style={{ borderColor: 'var(--scpa-status-warning)', backgroundColor: 'var(--scpa-surface)' }}
      >
        <span className="text-sm font-mono text-fg">{Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}</span>
        <button
          className="btn btn-danger text-sm inline-flex items-center gap-1"
          onClick={() => stopMutation.mutate()}
          disabled={stopMutation.isPending}
        >
          <Square className="w-4 h-4" />
          Stop
        </button>
      </div>
    );
  }

  return (
    <button
      className="btn btn-secondary text-sm inline-flex items-center gap-1"
      onClick={() => startMutation.mutate()}
      disabled={startMutation.isPending || !!currentEntry}
      title={currentEntry ? 'Stop other timer first' : 'Start timer'}
    >
      <Play className="w-4 h-4" />
      Start timer
    </button>
  );
}
